import { useEffect, useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { setGenericStaticPageTitle } from "../redux/modules/genericStaticPageTitle";
import {
  beginStaticPageLayout,
  resetStaticPageLayout,
  setStaticPageLayout
} from "../redux/modules/staticPageLayout";
import normalizeStaticPageTitle from "../lib/normalizeStaticPageTitle";
import useGetRequest from "./useGetRequest";

interface UseStaticPageContentProps {
  slug: String;
  locale: String;
}

function useStaticPageContent({ slug, locale }: UseStaticPageContentProps) {
  const urlPath = "app.staticPages.bySlug";
  const seoDataUrlPath = "app.staticSeoData.retrieve";
  const dispatch = useDispatch();

  const { response, loading, error } = useGetRequest({
    urlPath,
    variables: {
      slug
    },
    locale,
    skipAccessCheck: true
  });

  const { response: seoDataResponse, error: seoDataError } = useGetRequest({
    urlPath: seoDataUrlPath,
    variables: {
      slug
    },
    locale,
    skipAccessCheck: true
  });

  useEffect(() => {
    dispatch(
      setGenericStaticPageTitle(
        normalizeStaticPageTitle(seoDataResponse?.meta_title)
      )
    );

    return () => {
      dispatch(setGenericStaticPageTitle(null));
    };
  }, [dispatch, seoDataResponse?.meta_title]);

  useLayoutEffect(() => {
    dispatch(beginStaticPageLayout());

    return () => {
      dispatch(resetStaticPageLayout());
    };
  }, [dispatch, slug]);

  useEffect(() => {
    // Stay hidden until a successful payload arrives. Transient auth/network
    // errors must not briefly reveal chrome before a retry succeeds.
    if (loading || error || !response) {
      return;
    }

    dispatch(
      setStaticPageLayout({
        fullWidth: !!response.full_width,
        hideMenuAndSearch: !!response.hide_menu_and_search
      })
    );
  }, [
    dispatch,
    loading,
    error,
    response,
    response?.full_width,
    response?.hide_menu_and_search
  ]);

  return { response, loading, error, seoDataResponse };
}

export default useStaticPageContent;
